/**
 * Tenant detail shell (Phase 3 Action 37, refactored in Phase 5 Action 49).
 *
 * Lands at /fleet/:id and renders the persistent chrome for a tenant:
 *   • Breadcrumb + name + status pill
 *   • Slack install success/error banner (kept here so it's visible
 *     regardless of which tab the operator lands on after callback)
 *   • Tab strip (Overview | Health | Pipeline | Activity | Users |
 *     Config | Audit)
 *
 * Tab content renders through React Router's <Outlet />; the tabs are
 * nested routes registered in App.tsx. Passing the fetched tenant via
 * useOutletContext lets each tab component skip a round trip on the
 * name/slug/status header data.
 *
 * The old page's sections all moved into the tab components in
 * ./tenantDetail/*.tsx — this file used to hold ~600 lines of them.
 */

import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, NavLink, Outlet, useParams, useSearchParams } from 'react-router-dom';
import { fetchJson } from './tenantDetail/shared';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'provisioning' | 'active' | 'suspended' | 'trial' | 'deleted';
  primaryDomain: string | null;
  timezone: string;
  locale: string;
  activatedAt: string | null;
  createdAt: string;
}

interface SlackConnection {
  workspaceId: string;
  workspaceName: string;
  botUserId: string;
  connectedAt: string;
  connectedByOperatorId: string | null;
}

// Maps the backend's install_error codes to operator-facing copy.
const INSTALL_ERROR_COPY: Record<string, string> = {
  state_malformed:        'The install link was malformed. Please regenerate.',
  state_bad_signature:    'The install link signature was invalid. Please regenerate.',
  state_invalid_payload:  'The install link payload was invalid. Please regenerate.',
  state_expired:          'The install link expired. Please regenerate.',
  missing_code_or_state:  'Slack redirected without the expected parameters. Please retry.',
  slack_access_denied:    'Authorization was denied. Please retry.',
  exchange_threw:         'Network error while exchanging the code with Slack. Please retry.',
  credential_write_failed:'Could not save credentials. Please retry.',
  no_initial_config:      'Tenant configuration missing. Contact support.',
  config_update_failed:   'The credential was saved but the tenant config failed to update. Retrying the install should reconcile both.',
};

const STATUS_BADGE: Record<Tenant['status'], string> = {
  provisioning: 'bg-amber-100 text-amber-800',
  active:       'bg-emerald-100 text-emerald-800',
  trial:        'bg-amber-100 text-amber-800',
  suspended:    'bg-red-100 text-red-800',
  deleted:      'bg-slate-200 text-slate-700',
};

const TABS: Array<{ slug: string; label: string }> = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'health',   label: 'Health' },
  { slug: 'pipeline', label: 'Pipeline' },
  { slug: 'activity', label: 'Activity' },
  { slug: 'users',    label: 'Users' },
  { slug: 'config',   label: 'Config' },
  { slug: 'audit',    label: 'Audit' },
];

export default function TenantDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();

  const installSuccess = search.get('installed') === 'slack';
  const installError = search.get('install_error');

  const tenantQ = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => fetchJson<{ data: { tenant: Tenant } }>(`/api/provisioning/tenants/${id}`),
    enabled: Boolean(id),
  });

  // Slack connection is fetched here (and in the Config tab) so the
  // success banner can name the workspace. React Query dedupes the
  // requests by queryKey.
  const slackQ = useQuery({
    queryKey: ['tenant-slack', id],
    queryFn: () => fetchJson<{ data: { connection: SlackConnection | null } }>(`/api/provisioning/tenants/${id}/slack/connection`),
    enabled: Boolean(id),
  });

  // After a successful install, the callback redirects with
  // ?installed=slack. Invalidate the slack query so the freshly-written
  // connection is visible immediately.
  useEffect(() => {
    if (installSuccess) {
      queryClient.invalidateQueries({ queryKey: ['tenant-slack', id] });
    }
  }, [installSuccess, id, queryClient]);

  const installErrorMsg = useMemo(() => {
    if (!installError) return null;
    return INSTALL_ERROR_COPY[installError] ?? `Install failed: ${installError}`;
  }, [installError]);

  function dismissBanner() {
    search.delete('installed');
    search.delete('install_error');
    setSearch(search, { replace: true });
  }

  if (tenantQ.isLoading) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Loading tenant…
      </div>
    );
  }
  if (tenantQ.isError || !tenantQ.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load tenant: {tenantQ.error instanceof Error ? tenantQ.error.message : 'unknown error'}
        <div className="mt-2">
          <Link to="/fleet" className="text-red-700 underline">Back to Fleet</Link>
        </div>
      </div>
    );
  }

  const tenant = tenantQ.data.data.tenant;
  const slackConnection = slackQ.data?.data.connection ?? null;

  return (
    <div>
      <div className="mb-6">
        <Link to="/fleet" className="text-sm text-slate-500 hover:text-slate-700">← Fleet</Link>
        <div className="mt-2 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{tenant.name}</h1>
            <p className="mt-1 text-sm text-slate-500 font-mono">{tenant.slug}</p>
          </div>
          <span className={clsx('inline-flex rounded-full px-3 py-1 text-sm font-medium', STATUS_BADGE[tenant.status])}>
            {tenant.status}
          </span>
        </div>
      </div>

      {installSuccess && slackConnection && (
        <div className="mb-4 flex items-start justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <div>
            Slack connected to workspace <strong>{slackConnection.workspaceName}</strong>.
          </div>
          <button type="button" onClick={dismissBanner} className="ml-4 text-emerald-700 underline">
            Dismiss
          </button>
        </div>
      )}
      {installErrorMsg && (
        <div className="mb-4 flex items-start justify-between rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <div>{installErrorMsg}</div>
          <button type="button" onClick={dismissBanner} className="ml-4 text-red-700 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Tab strip. Uses NavLink so React Router applies the active
          state automatically based on the current URL — no manual
          matching. */}
      <div className="mb-6 border-b border-slate-200">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Tabs">
          {TABS.map((t) => (
            <NavLink
              key={t.slug}
              to={`/fleet/${tenant.id}/${t.slug}`}
              className={({ isActive }) =>
                clsx(
                  'whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet context={{ tenant }} />
    </div>
  );
}
